import type { InfiniteData } from "@tanstack/react-query";
import type {
  DropshipLineSortMode,
  DropshipManufacturerBucketSummary,
  DropshipV2Line,
  DropshipV2LineChanges,
  DropshipV2LineGroup,
  DropshipV2LinePage,
} from "./types";

export function orderedGroups(
  groups: DropshipV2LineGroup[],
  mode: DropshipLineSortMode,
) {
  if (mode === "quote") return groups;
  return [...groups].sort((left, right) => {
    const manufacturer = String(
      left.root.manufacturer_name || "Manufacturer needs review",
    ).localeCompare(
      String(right.root.manufacturer_name || "Manufacturer needs review"),
    );
    return manufacturer || left.root.root_order - right.root.root_order;
  });
}

export function flattenLines(groups: DropshipV2LineGroup[]) {
  return groups.flatMap((group) => [group.root, ...group.children]);
}

function numeric(value: string | null | undefined) {
  const parsed = Number(value ?? 0);
  return Number.isFinite(parsed) ? parsed : 0;
}

type ManufacturerBucket = DropshipManufacturerBucketSummary & {
  groups: DropshipV2LineGroup[];
  quantity: number;
  sell: number;
  net: number;
  list: number;
  sellTotal: number;
  discounts: Set<string>;
};

// Display summaries include every line, unlike the included-only group totals below.
export function groupByManufacturer(
  groups: DropshipV2LineGroup[],
): ManufacturerBucket[] {
  const buckets = new Map<string, ManufacturerBucket>();
  for (const group of groups) {
    const key = group.root.manufacturer_id
      ? `id:${group.root.manufacturer_id}`
      : `name:${group.root.manufacturer_name || "needs-review"}`;
    const bucket = buckets.get(key) ?? {
      key,
      name: group.root.manufacturer_name || "Manufacturer needs review",
      manufacturerId: group.root.manufacturer_id ?? null,
      rootLineUuids: [],
      groups: [],
      quantity: 0,
      sell: 0,
      net: 0,
      list: 0,
      sellTotal: 0,
      discounts: new Set<string>(),
    };
    const lines = [group.root, ...group.children];
    const discounts = lines.map((line) => line.discount_chain?.trim() || "—");
    bucket.groups.push(group);
    bucket.rootLineUuids.push(group.root.uuid);
    bucket.quantity += lines.reduce(
      (sum, line) => sum + numeric(line.quantity),
      0,
    );
    bucket.sell += lines.reduce(
      (sum, line) => sum + numeric(line.sell_price),
      0,
    );
    bucket.net += lines.reduce((sum, line) => sum + numeric(line.net_price), 0);
    bucket.list += lines.reduce(
      (sum, line) => sum + numeric(line.list_price),
      0,
    );
    bucket.sellTotal += lines.reduce(
      (sum, line) => sum + numeric(line.line_total),
      0,
    );
    discounts.forEach((discount) => bucket.discounts.add(discount));
    buckets.set(key, bucket);
  }
  return [...buckets.values()];
}

export function replaceLineInPages(
  data: InfiniteData<DropshipV2LinePage> | undefined,
  line: DropshipV2Line,
): InfiniteData<DropshipV2LinePage> | undefined {
  if (!data) return data;
  return {
    ...data,
    pages: data.pages.map((page) => ({
      ...page,
      items: page.items.map((group) => {
        if (group.root.uuid === line.uuid)
          return recalculateGroup({ ...group, root: line });
        if (group.children.some((child) => child.uuid === line.uuid)) {
          return recalculateGroup({
            ...group,
            children: group.children.map((child) =>
              child.uuid === line.uuid ? line : child,
            ),
          });
        }
        return group;
      }),
    })),
  };
}

function replaceLinesInPages(
  data: InfiniteData<DropshipV2LinePage> | undefined,
  lines: DropshipV2Line[],
) {
  return lines.reduce<InfiniteData<DropshipV2LinePage> | undefined>(
    (current, line) => replaceLineInPages(current, line),
    data,
  );
}

function optimisticLine(
  line: DropshipV2Line,
  changes: DropshipV2LineChanges,
): DropshipV2Line {
  const optimistic: DropshipV2Line = {
    ...line,
    ...changes,
    quantity:
      changes.quantity === undefined
        ? line.quantity
        : changes.quantity == null
          ? null
          : String(changes.quantity),
    list_price:
      changes.list_price === undefined
        ? line.list_price
        : changes.list_price == null
          ? null
          : String(changes.list_price),
    net_price:
      changes.net_price === undefined
        ? line.net_price
        : changes.net_price == null
          ? null
          : String(changes.net_price),
    sell_price:
      changes.sell_price === undefined
        ? line.sell_price
        : changes.sell_price == null
          ? null
          : String(changes.sell_price),
    display_number: changes.display_number ?? line.display_number,
    line_kind: changes.line_kind ?? line.line_kind,
    disposition: changes.disposition ?? line.disposition,
    // The source picker spins until the server's priced line replaces this one (USA-1574).
    warehouse_source: line.warehouse_source,
    revision: line.revision + 1,
  };
  if ("quantity" in changes || "sell_price" in changes) {
    const quantity = Number(optimistic.quantity ?? 0);
    const sellPrice = Number(optimistic.sell_price ?? 0);
    optimistic.line_total = Number.isFinite(quantity * sellPrice)
      ? (quantity * sellPrice).toFixed(2)
      : optimistic.line_total;
  }
  return optimistic;
}

export function optimisticallyPatchLineInPages(
  data: InfiniteData<DropshipV2LinePage> | undefined,
  line: DropshipV2Line,
  changes: DropshipV2LineChanges,
) {
  const groups = data?.pages.flatMap((page) => page.items) ?? [];
  const targetGroup = groups.find(
    (group) =>
      group.root.uuid === line.uuid ||
      group.children.some((child) => child.uuid === line.uuid),
  );
  const cachedLine = targetGroup
    ? [targetGroup.root, ...targetGroup.children].find(
        (item) => item.uuid === line.uuid,
      )
    : undefined;
  const sourceLine = cachedLine ?? line;
  let optimisticChanges = changes;
  if (sourceLine.depth > 0 && targetGroup && changes.display_number != null) {
    const rootNumber = targetGroup.root.display_number;
    const requestedNumber = changes.display_number;
    if (
      !requestedNumber.startsWith(`${rootNumber}.`) &&
      !requestedNumber.startsWith(`${rootNumber}-`)
    ) {
      const suffix = /^[^.-]+([.-].+)$/.exec(requestedNumber)?.[1];
      optimisticChanges = {
        ...changes,
        display_number: `${rootNumber}${suffix ?? `.${requestedNumber}`}`,
      };
    }
  }
  const cascadesDisposition =
    sourceLine.depth === 0 &&
    (optimisticChanges.disposition === "include_on_po" ||
      optimisticChanges.disposition === "internal_only");
  const cascadesDisplayNumber =
    sourceLine.depth === 0 &&
    optimisticChanges.display_number != null &&
    optimisticChanges.display_number !== sourceLine.display_number;
  const previousLines =
    (cascadesDisposition || cascadesDisplayNumber) && targetGroup
      ? [targetGroup.root, ...targetGroup.children]
      : [sourceLine];
  const optimisticLines = previousLines.map((item) =>
    item.uuid === sourceLine.uuid
      ? optimisticLine(item, optimisticChanges)
      : cascadesDisplayNumber
        ? optimisticLine(item, {
            display_number: item.display_number.startsWith(
              `${sourceLine.display_number}.`,
            )
              ? `${optimisticChanges.display_number}${item.display_number.slice(sourceLine.display_number.length)}`
              : item.display_number.startsWith(`${sourceLine.display_number}-`)
                ? `${optimisticChanges.display_number}${item.display_number.slice(sourceLine.display_number.length)}`
                : `${optimisticChanges.display_number}.${item.child_order}`,
          })
        : optimisticLine(item, {
            disposition: optimisticChanges.disposition,
            disposition_reason: null,
          }),
  );
  return {
    data: replaceLinesInPages(data, optimisticLines),
    previousLines,
  };
}

export function restoreLinesInPages(
  data: InfiniteData<DropshipV2LinePage> | undefined,
  lines: DropshipV2Line[],
) {
  return replaceLinesInPages(data, lines);
}

function recalculateGroup(group: DropshipV2LineGroup): DropshipV2LineGroup {
  const included = [group.root, ...group.children].filter(
    (line) => line.disposition === "include_on_po",
  );
  return {
    ...group,
    group_sell_total: included
      .reduce(
        (sum, line) =>
          sum + Number(line.sell_price ?? 0) * Number(line.quantity ?? 0),
        0,
      )
      .toFixed(2),
    group_net_total: included
      .reduce(
        (sum, line) =>
          sum + Number(line.net_price ?? 0) * Number(line.quantity ?? 0),
        0,
      )
      .toFixed(2),
  };
}

// Insertion deliberately does not apply disposition filtering or fixed precision.
export function insertLineInPages(
  data: InfiniteData<DropshipV2LinePage> | undefined,
  line: DropshipV2Line,
): InfiniteData<DropshipV2LinePage> | undefined {
  if (!data || data.pages.length === 0) return data;
  if (line.depth === 0) {
    const pages = [...data.pages];
    const lastIndex = pages.length - 1;
    const page = pages[lastIndex];
    if (!page) return data;
    pages[lastIndex] = {
      ...page,
      items: [
        ...page.items,
        {
          root: line,
          children: [],
          group_sell_total: String(
            Number(line.sell_price ?? 0) * Number(line.quantity ?? 0),
          ),
          group_net_total: String(
            Number(line.net_price ?? 0) * Number(line.quantity ?? 0),
          ),
          pricing_mode: "explicit",
        },
      ],
    };
    return { ...data, pages };
  }
  return {
    ...data,
    pages: data.pages.map((page) => ({
      ...page,
      items: page.items.map((group) =>
        group.root.root_source_key === line.root_source_key
          ? {
              ...group,
              children: [...group.children, line].sort(
                (left, right) =>
                  left.child_order - right.child_order || left.id - right.id,
              ),
              group_sell_total: String(
                Number(group.group_sell_total) +
                  Number(line.sell_price ?? 0) * Number(line.quantity ?? 0),
              ),
              group_net_total: String(
                Number(group.group_net_total) +
                  Number(line.net_price ?? 0) * Number(line.quantity ?? 0),
              ),
            }
          : group,
      ),
    })),
  };
}

export function removeLinesFromPages(
  data: InfiniteData<DropshipV2LinePage> | undefined,
  removedUuids: Set<string>,
): InfiniteData<DropshipV2LinePage> | undefined {
  if (!data) return data;
  return {
    ...data,
    pages: data.pages.map((page) => ({
      ...page,
      items: page.items
        .filter((group) => !removedUuids.has(group.root.uuid))
        .map((group) =>
          recalculateGroup({
            ...group,
            children: group.children.filter(
              (line) => !removedUuids.has(line.uuid),
            ),
          }),
        ),
    })),
  };
}
